import { useEffect, useRef } from 'react';

import { useIsAdminOrManagerMode } from './useIsAdminOrManagerMode';

// How often an unattended display checks whether a newer bundle was deployed.
const VERSION_POLL_INTERVAL_MS = 5 * 60 * 1000;

// CRA writes a fresh `asset-manifest.json` (hashed filenames) on every
// production build, so its contents change whenever a new bundle is deployed.
async function fetchBuildSignature(): Promise<string | null> {
  try {
    const res = await fetch(
      `${process.env.PUBLIC_URL || ''}/asset-manifest.json`,
      {
        cache: 'no-store',
      },
    );
    if (!res.ok) return null;
    const manifest = await res.json();
    return JSON.stringify(manifest.entrypoints ?? manifest.files ?? manifest);
  } catch {
    return null;
  }
}

/**
 * Unattended wall displays stay open for days, so an old bundle keeps running
 * and never picks up newly deployed fields/UI. This hook polls the build
 * manifest and hard-reloads the page when it changes — but only for non-admin
 * (display) sessions, so an admin editing settings is never reloaded out from
 * under their work. No-op outside production.
 */
export function useAutoReloadOnNewBuild() {
  const isAdminOrManager = useIsAdminOrManagerMode();
  const baselineRef = useRef<string | null>(null);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return;
    if (isAdminOrManager) return;

    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | undefined;

    (async () => {
      baselineRef.current = await fetchBuildSignature();
      if (cancelled) return;
      timer = setInterval(async () => {
        const current = await fetchBuildSignature();
        if (
          !cancelled &&
          current &&
          baselineRef.current &&
          current !== baselineRef.current
        ) {
          window.location.reload();
        }
      }, VERSION_POLL_INTERVAL_MS);
    })();

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [isAdminOrManager]);
}
